// F-U / F-W: the Year · Exhibition · Zone · Department filter bar shared by
// the Exhibits and Zones pages (`keys` picks which filters a page shows).
// Desktop: a row of checkbox dropdowns. Phone: a "Filters (n)" button that
// opens a bottom sheet with the same lists as collapsible sections.
// Active selections show as removable chips underneath.

import { useState, type FC, type PropsWithChildren } from "react";
import { X } from "lucide-react";
import { Button } from "@shared/components/ui/button";
import {
  Sheet,
  SheetContent as RawSheetContent,
  SheetHeader as RawSheetHeader,
  SheetTitle as RawSheetTitle,
} from "@shared/components/ui/sheet";
import MultiSelectFilter from "@staff/components/MultiSelectFilter";
import {
  FILTER_KEYS,
  activeFilterCount,
  optionLabel,
  type ExhibitFilters,
  type FilterKey,
  type FilterOptions,
} from "./listFilters";

// sheet.jsx is untyped; same narrow casts as AuditDetailDrawer.
type SheetDivProps = PropsWithChildren<{
  className?: string;
  "data-testid"?: string;
}>;
const SheetContent = RawSheetContent as unknown as FC<
  SheetDivProps & { side?: "top" | "bottom" | "left" | "right" }
>;
const SheetHeader = RawSheetHeader as unknown as FC<SheetDivProps>;
const SheetTitle = RawSheetTitle as unknown as FC<SheetDivProps>;

const FILTER_META: Record<
  FilterKey,
  { label: string; searchable: boolean; testId: string }
> = {
  years: { label: "Year", searchable: false, testId: "filter-year" },
  exhibitions: {
    label: "Exhibition",
    searchable: true,
    testId: "filter-exhibition",
  },
  zones: { label: "Zone", searchable: true, testId: "filter-zone" },
  departments: {
    label: "Department",
    searchable: false,
    testId: "filter-department",
  },
};

export interface ListFilterBarProps {
  filters: ExhibitFilters;
  options: FilterOptions;
  onChange: (next: ExhibitFilters) => void;
  /** Clears the four filters AND the search box (Status is kept). */
  onClearAll: () => void;
  canClear: boolean;
  shownCount: number;
  /** Which filters this page offers (default: all four). */
  keys?: FilterKey[];
}

const ListFilterBar: FC<ListFilterBarProps> = ({
  filters,
  options,
  onChange,
  onClearAll,
  canClear,
  shownCount,
  keys = FILTER_KEYS,
}) => {
  const [sheetOpen, setSheetOpen] = useState(false);
  const active = activeFilterCount(filters);

  const set = (key: FilterKey) => (values: string[]) =>
    onChange({ ...filters, [key]: values });

  const renderFilters = (layout: "dropdown" | "inline") =>
    keys.map((key) => (
      <MultiSelectFilter
        key={key}
        label={FILTER_META[key].label}
        options={options[key]}
        selected={filters[key]}
        onChange={set(key)}
        searchable={FILTER_META[key].searchable}
        layout={layout}
        testId={
          layout === "inline"
            ? `${FILTER_META[key].testId}-sheet`
            : FILTER_META[key].testId
        }
      />
    ));

  const clearLink = canClear && (
    <button
      type="button"
      onClick={onClearAll}
      className="text-sm text-primary hover:underline"
      data-testid="clear-filters"
    >
      Clear filters
    </button>
  );

  return (
    <div className="space-y-3">
      {/* Desktop row */}
      <div
        className="hidden md:flex flex-wrap items-center gap-2"
        data-testid="filter-bar"
      >
        {renderFilters("dropdown")}
        {clearLink}
      </div>

      {/* Phone: one button + bottom sheet */}
      <div className="md:hidden">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setSheetOpen(true)}
          data-testid="filters-sheet-button"
        >
          Filters{active > 0 ? ` (${active})` : ""}
        </Button>
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetContent
            side="bottom"
            className="max-h-[85vh] overflow-y-auto"
            data-testid="filters-sheet"
          >
            <SheetHeader>
              <SheetTitle>Filters</SheetTitle>
            </SheetHeader>
            <div className="space-y-2 py-4">{renderFilters("inline")}</div>
            <div className="flex items-center justify-between">
              <Button
                type="button"
                variant="ghost"
                onClick={onClearAll}
                disabled={!canClear}
                data-testid="filters-sheet-clear"
              >
                Clear
              </Button>
              <Button
                type="button"
                onClick={() => setSheetOpen(false)}
                data-testid="filters-sheet-show"
              >
                Show {shownCount} results
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Chips */}
      {active > 0 && (
        <div
          className="flex flex-wrap items-center gap-2 text-sm"
          data-testid="filter-chips"
        >
          <span className="text-muted-foreground">Active:</span>
          {keys.flatMap((key) =>
            filters[key].map((value) => (
              <span
                key={`${key}:${value}`}
                className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 bg-muted/40"
              >
                <span className="text-muted-foreground">
                  {FILTER_META[key].label}:
                </span>
                {optionLabel(options, key, value)}
                <button
                  type="button"
                  onClick={() =>
                    onChange({
                      ...filters,
                      [key]: filters[key].filter((v) => v !== value),
                    })
                  }
                  aria-label={`Remove ${FILTER_META[key].label} ${optionLabel(options, key, value)}`}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid={`chip-remove-${key}-${value}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )),
          )}
        </div>
      )}
    </div>
  );
};

export default ListFilterBar;
